import type { JobDetail } from '@/../../shared/types/job';
import type { JobNoteWithUser } from '@jobos/shared';
import { formatClientName, formatManagerName } from '@jobos/shared';
import { getJobStatusLabel } from '@jobos/shared/constants/job-statuses';
import { JOB_CARD_STATUS_CONFIG, JOB_INVOICE_STATUS_CONFIG } from '@jobos/shared';
import { useJobNotes } from '@/hooks/useJobNotes';
import { unknownStatusConfig } from '@/components/shared/unknownStatus';

interface JobDetailPrintViewProps {
  job: JobDetail;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr + 'T00:00:00');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${date.getFullYear()}`;
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatBillingType(type: string | null | undefined): string {
  if (!type) return '\u2014';
  if (type === 'one_time') return 'One-Time';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function formatInvoiceType(type: string | null | undefined): string {
  if (!type) return '\u2014';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function formatDisposition(disposition: string | null | undefined): string {
  if (!disposition) return '\u2014';
  if (disposition === 'not_awarded') return 'Not Awarded';
  return disposition.charAt(0).toUpperCase() + disposition.slice(1);
}

interface InvoiceHistoryEntry {
  period: string;
  number: string;
}

function parseInvoiceHistory(invoiceNumber: string | null | undefined): InvoiceHistoryEntry[] {
  if (!invoiceNumber) return [];
  try {
    const parsed = JSON.parse(invoiceNumber);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // Not JSON
  }
  return [];
}

function formatPeriodDisplay(period: string, billingType: string | null | undefined): string {
  if (billingType === 'quarterly') {
    return period.replace('-', ' ');
  }
  const parts = period.split('-');
  const year = parts[0] || '2025';
  const month = parts[1] || '01';
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function buildServiceAddress(job: JobDetail): string {
  const parts: string[] = [];
  if (job.location.addressLine1) parts.push(job.location.addressLine1);
  if (job.location.addressLine2) parts.push(job.location.addressLine2);
  if (job.location.city) parts.push(job.location.city);
  if (parts.length === 0) return '\u2014';
  return parts.join(', ');
}

function buildBillingAddress(job: JobDetail): string | null {
  const hasAny = job.billingAddressLine1 || job.billingAddressLine2 || job.billingCity || job.billingState || job.billingZip;
  if (!hasAny) return null;
  const parts: string[] = [];
  if (job.billingAddressLine1) parts.push(job.billingAddressLine1);
  if (job.billingAddressLine2) parts.push(job.billingAddressLine2);
  const cityStateZip = [job.billingCity, job.billingState].filter(Boolean).join(', ');
  const withZip = [cityStateZip, job.billingZip].filter(Boolean).join(' ');
  if (withZip) parts.push(withZip);
  if (job.billingAddressLabel) {
    return `${job.billingAddressLabel} — ${parts.join(', ')}`;
  }
  return parts.join(', ');
}

function PrintField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="print-label">{label}:</span>
      <span className="print-value">{value}</span>
    </div>
  );
}

function NotesSection({ notes }: { notes: JobNoteWithUser[] }) {
  if (notes.length === 0) {
    return <p className="text-sm italic">No notes.</p>;
  }

  return (
    <div className="space-y-3">
      {notes.map((note, i) => (
        <div key={note.id}>
          <div className="flex gap-2 text-sm">
            <span className="font-semibold">{note.user.name}</span>
            <span>&mdash;</span>
            <span>{formatDateTime(note.createdAt)}</span>
          </div>
          <p className="text-sm whitespace-pre-wrap mt-0.5">{note.message}</p>
          {i < notes.length - 1 && <hr className="mt-3 border-gray-300" />}
        </div>
      ))}
    </div>
  );
}

export function JobDetailPrintView({ job }: JobDetailPrintViewProps) {
  const { data: notes } = useJobNotes(job.id);

  // ?? — `jobs.job_card_status` and `jobs.invoice_status` are plain text columns
  // with no database constraint, so the typed unions are a claim rather than a
  // guarantee. Indexing these Records and dereferencing blind is worse here than
  // in a list badge: this component is rendered unconditionally by
  // JobDetailOverview, so an unrecognised value throws during render and the
  // error boundary replaces the ENTIRE job page.
  //
  // Not hypothetical. The shared dev database held one job at invoice_status
  // `partially_paid` — a value an unmerged branch writes — and every branch off
  // main died on that job. Same hole the three list badges were hardened against.
  const jobCardConfig =
    JOB_CARD_STATUS_CONFIG[job.jobCardStatus] ?? unknownStatusConfig(job.jobCardStatus);
  const invoiceConfig =
    JOB_INVOICE_STATUS_CONFIG[job.invoiceStatus] ?? unknownStatusConfig(job.invoiceStatus);

  const clientName = formatClientName(job.client.name, job.client.firstName, job.client.lastName);
  const managerName = job.manager
    ? formatManagerName(job.manager.name, job.manager.firstName, job.manager.lastName)
    : null;
  const isRecurring = job.billingType === 'monthly' || job.billingType === 'quarterly';
  const invoiceHistory = isRecurring ? parseInvoiceHistory(job.invoiceNumber) : [];

  return (
    <div className="print-view">
      {/* Header */}
      <div className="print-section border-b-2 border-black pb-3 mb-4">
        <div className="flex justify-between items-start">
          <h1 className="text-2xl font-bold tracking-tight">{job.jobCode}</h1>
          <div className="flex gap-3 text-sm">
            <span className="border border-gray-500 px-2 py-0.5 rounded">
              {getJobStatusLabel(job.status)}
            </span>
            <span className="border border-gray-500 px-2 py-0.5 rounded">
              Job Card: {jobCardConfig.label}
            </span>
            <span className="border border-gray-500 px-2 py-0.5 rounded">
              Invoice: {job.invoiceStatus === 'invoice_sent' && job.lastInvoicedDate
                ? `Sent ${job.lastInvoicedDate}`
                : invoiceConfig.label}
            </span>
          </div>
        </div>
        <div className="mt-1 text-sm">
          <span>{clientName}</span>
          {managerName && <span> &bull; {managerName}</span>}
        </div>
        <div className="text-sm text-gray-600 mt-0.5">
          {buildServiceAddress(job)}
          {job.serviceAddressNote && <span className="ml-2 italic">({job.serviceAddressNote})</span>}
        </div>
      </div>

      {/* Project Info */}
      <div className="print-section border border-gray-400 rounded p-3 mb-4">
        <h2 className="print-section-title">Project Info</h2>
        <div className="space-y-1 text-sm">
          <PrintField label="Type" value={job.customProjectType || job.projectType || '\u2014'} />
          <PrintField label="Disposition" value={formatDisposition(job.disposition)} />
        </div>
        {job.description && (
          <div className="mt-2 text-sm">
            <span className="print-label">Description:</span>
            <p className="mt-0.5">{job.description}</p>
          </div>
        )}
      </div>

      {/* Dates + Billing side by side */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="print-section border border-gray-400 rounded p-3">
          <h2 className="print-section-title">Important Dates</h2>
          <div className="space-y-1 text-sm">
            <PrintField label="Notice" value={formatDate(job.noticeDate)} />
            <PrintField label="Estimate Sent" value={formatDate(job.estimateSendDate)} />
            <PrintField label="Start" value={formatDate(job.startDate)} />
            <PrintField label="Completed" value={formatDate(job.completedDate)} />
          </div>
        </div>

        <div className="print-section border border-gray-400 rounded p-3">
          <h2 className="print-section-title">Billing</h2>
          <div className="space-y-1 text-sm">
            {buildBillingAddress(job) && (
              <PrintField label="Billing Address" value={buildBillingAddress(job)!} />
            )}
            <PrintField label="Job Card" value={jobCardConfig.label} />
            <PrintField
              label="Invoice"
              value={
                job.invoiceStatus === 'invoice_sent' && job.lastInvoicedDate
                  ? `Sent ${job.lastInvoicedDate}`
                  : invoiceConfig.label
              }
            />
            <PrintField label="Type" value={formatBillingType(job.billingType)} />
            <PrintField label="Invoice Type" value={formatInvoiceType(job.invoiceType)} />
            {isRecurring && (
              <>
                <PrintField label="Rate" value={job.billingRate || '\u2014'} />
                <PrintField label="Last Invoiced" value={formatDate(job.lastInvoicedDate)} />
              </>
            )}
            {!isRecurring && (
              <>
                <PrintField label="Invoice #" value={job.invoiceNumber || '\u2014'} />
                <PrintField label="Invoice Sent" value={formatDate(job.lastInvoicedDate)} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Invoice History for recurring */}
      {isRecurring && invoiceHistory.length > 0 && (
        <div className="print-section border border-gray-400 rounded p-3 mb-4">
          <h2 className="print-section-title">Invoice History</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-400">
                <th className="text-left py-1 font-semibold">Period</th>
                <th className="text-left py-1 font-semibold">Invoice #</th>
              </tr>
            </thead>
            <tbody>
              {invoiceHistory.map((entry) => (
                <tr key={entry.period} className="border-b border-gray-200">
                  <td className="py-1">{formatPeriodDisplay(entry.period, job.billingType)}</td>
                  <td className="py-1">{entry.number}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes */}
      <div className="print-section border border-gray-400 rounded p-3 mb-4">
        <h2 className="print-section-title">Notes</h2>
        <NotesSection notes={notes || []} />
      </div>

      {/* Footer */}
      <div className="text-xs text-gray-500 mt-6 border-t border-gray-300 pt-2 flex justify-between">
        <span>Printed {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
      </div>
    </div>
  );
}
